/**
 * Created: 2026-11-03
 * Initial commit: feat(iot): define control points from the admin devices page
 *
 * Dialog for app admins to list, add, edit and delete the control points
 * (relays on Modbus IO modules) wired to one device. Each change is saved,
 * then pushed to the device by the bridge (ADR-0007); when the push fails the
 * change is still saved and the device picks it up on its next connect.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  CreateIotControlPointDocument,
  DeleteIotControlPointDocument,
  IotDeviceControlPointsDocument,
  type IotDeviceControlPointsQuery,
  IotSiteCourtsDocument,
  UpdateIotControlPointDocument,
} from '../gql/graphql'

gql`
  query IotDeviceControlPoints($deviceId: UUID!) {
    iotDeviceControlPoints(deviceId: $deviceId) {
      controlPointId
      label
      court {
        id
        name
      }
      bootDefault
      slaveAddress
      coil
    }
  }
`

gql`
  query IotSiteCourts($siteId: UUID!) {
    site(siteId: $siteId) {
      id
      courts {
        id
        name
      }
    }
  }
`

// Each mutation spells out its selection: the client preset masks fragment
// fields, which would hide configPushed and message from the result.
gql`
  mutation CreateIotControlPoint($input: CreateIotControlPointInput!) {
    createIotControlPoint(controlPointInput: $input) {
      ... on IotControlPointChanged {
        configPushed
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }

  mutation UpdateIotControlPoint($input: UpdateIotControlPointInput!) {
    updateIotControlPoint(controlPointInput: $input) {
      ... on IotControlPointChanged {
        configPushed
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }

  mutation DeleteIotControlPoint($controlPointId: UUID!) {
    deleteIotControlPoint(controlPointId: $controlPointId) {
      ... on IotControlPointChanged {
        configPushed
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

type ControlPoint = IotDeviceControlPointsQuery['iotDeviceControlPoints'][number]

type ChangeResult =
  | { __typename: 'IotControlPointChanged'; configPushed: boolean }
  | { __typename: 'GraphqlRequestError'; message?: string | null }

/** Form state. `doNumber` is the IO module's panel label `DO<n>`, 1-based;
 * the stored Modbus coil is `doNumber - 1`. Numbers are kept as strings so a
 * cleared field stays empty instead of snapping to 0. */
interface FormState {
  controlPointId: string | null
  label: string
  courtId: string
  bootDefault: boolean
  slaveAddress: string
  doNumber: string
}

const EMPTY_FORM: FormState = {
  controlPointId: null,
  label: '',
  courtId: '',
  bootDefault: false,
  slaveAddress: '1',
  doNumber: '',
}

function toForm(point: ControlPoint): FormState {
  return {
    controlPointId: point.controlPointId,
    label: point.label,
    courtId: point.court?.id ?? '',
    bootDefault: point.bootDefault ?? false,
    slaveAddress: point.slaveAddress?.toString() ?? '',
    doNumber: point.coil != null ? (point.coil + 1).toString() : '',
  }
}

function parseIntInRange(value: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(value.trim())) return null
  const n = Number(value)
  return n >= min && n <= max ? n : null
}

/** Report a mutation result; returns whether the change was saved. */
function reportResult(result: ChangeResult | undefined, done: string): boolean {
  if (!result) return false
  if (result.__typename === 'GraphqlRequestError') {
    message.error(result.message ?? '保存失败')
    return false
  }
  if (result.configPushed) {
    message.success(done, { autoHide: 2000 })
  } else {
    message.warn(`${done}，但未能推送到设备。设备下次连接时会自动获取新配置。`)
  }
  return true
}

interface IotControlPointsDialogProps {
  open: boolean
  onClose: () => void
  deviceId: string
  siteId: string
  siteName: string
}

export default function IotControlPointsDialog({
  open,
  onClose,
  deviceId,
  siteId,
  siteName,
}: IotControlPointsDialogProps) {
  const { data, loading, error, refetch } = useQuery(IotDeviceControlPointsDocument, {
    variables: { deviceId },
    skip: !open,
    fetchPolicy: 'network-only',
  })
  const { data: courtsData } = useQuery(IotSiteCourtsDocument, {
    variables: { siteId },
    skip: !open,
  })
  const [createPoint, { loading: creating }] = useMutation(
    CreateIotControlPointDocument,
  )
  const [updatePoint, { loading: updating }] = useMutation(
    UpdateIotControlPointDocument,
  )
  const [deletePoint, { loading: deleting }] = useMutation(
    DeleteIotControlPointDocument,
  )
  const [form, setForm] = useState<FormState | null>(null)

  const points = data?.iotDeviceControlPoints ?? []
  const courts = courtsData?.site.courts ?? []
  const saving = creating || updating

  const label = form?.label.trim() ?? ''
  const slaveAddress = form ? parseIntInRange(form.slaveAddress, 1, 247) : null
  const doNumber = form ? parseIntInRange(form.doNumber, 1, 65536) : null
  const formValid = label !== '' && slaveAddress !== null && doNumber !== null

  const handleSave = async () => {
    if (!form || slaveAddress === null || doNumber === null || !label) return
    const fields = {
      label,
      courtId: form.courtId || null,
      bootDefault: form.bootDefault,
      slaveAddress,
      coil: doNumber - 1,
    }
    try {
      let saved: boolean
      if (form.controlPointId) {
        const { data: result } = await updatePoint({
          variables: { input: { controlPointId: form.controlPointId, ...fields } },
        })
        saved = reportResult(result?.updateIotControlPoint, '控制点已更新')
      } else {
        const { data: result } = await createPoint({
          variables: { input: { deviceId, ...fields } },
        })
        saved = reportResult(result?.createIotControlPoint, '控制点已添加')
      }
      if (saved) {
        setForm(null)
        refetch()
      }
    } catch (err) {
      message.error(`保存失败: ${err instanceof Error ? err.message : err}`)
    }
  }

  const handleDelete = (point: ControlPoint) => {
    message.prompt(
      `确定删除控制点「${point.label}」吗？设备将不再接受该控制点的指令。`,
      {
        title: '删除控制点',
        yesLabel: '删除',
        noLabel: '取消',
        onYesButton: async () => {
          try {
            const { data: result } = await deletePoint({
              variables: { controlPointId: point.controlPointId },
            })
            if (reportResult(result?.deleteIotControlPoint, '控制点已删除')) {
              refetch()
            }
          } catch (err) {
            message.error(`删除失败: ${err instanceof Error ? err.message : err}`)
          }
        },
      },
    )
  }

  const handleClose = () => {
    setForm(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>控制点 · {siteName}</DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: 'monospace', mb: 2 }}
        >
          设备 {deviceId}
        </Typography>

        {!data && loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : !data && error ? (
          <Typography color="error">加载控制点失败: {error.message}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>名称</TableCell>
                  <TableCell>场地</TableCell>
                  <TableCell align="center">从站地址</TableCell>
                  <TableCell align="center">输出口</TableCell>
                  <TableCell align="center">开机状态</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {points.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">暂无控制点</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  points.map((point) => (
                    <TableRow key={point.controlPointId}>
                      <TableCell>{point.label}</TableCell>
                      <TableCell>{point.court?.name ?? '未分配'}</TableCell>
                      <TableCell align="center">{point.slaveAddress ?? '-'}</TableCell>
                      <TableCell align="center">
                        {point.coil != null ? `DO${point.coil + 1}` : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {point.bootDefault == null
                          ? '未设置'
                          : point.bootDefault
                            ? '开'
                            : '关'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="编辑">
                          <IconButton
                            size="small"
                            aria-label={`编辑 ${point.label}`}
                            onClick={() => setForm(toForm(point))}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="删除">
                          <IconButton
                            size="small"
                            aria-label={`删除 ${point.label}`}
                            disabled={deleting}
                            onClick={() => handleDelete(point)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {form ? (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              handleSave()
            }}
            sx={{
              mt: 3,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <Typography variant="subtitle1" sx={{ gridColumn: '1 / -1' }}>
              {form.controlPointId ? '编辑控制点' : '添加控制点'}
            </Typography>
            <TextField
              label="名称"
              required
              autoFocus
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              error={form.label !== '' && label === ''}
            />
            <TextField
              select
              label="场地"
              value={form.courtId}
              onChange={(e) => setForm({ ...form, courtId: e.target.value })}
            >
              <MenuItem value="">未分配</MenuItem>
              {courts.map((court) => (
                <MenuItem key={court.id} value={court.id}>
                  {court.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="从站地址"
              required
              inputMode="numeric"
              value={form.slaveAddress}
              onChange={(e) => setForm({ ...form, slaveAddress: e.target.value })}
              error={form.slaveAddress !== '' && slaveAddress === null}
              helperText="IO 模块的 Modbus 从站地址，1-247"
            />
            <TextField
              label="输出口 DO"
              required
              inputMode="numeric"
              value={form.doNumber}
              onChange={(e) => setForm({ ...form, doNumber: e.target.value })}
              error={form.doNumber !== '' && doNumber === null}
              helperText="IO 模块面板上的编号，如 DO1 填 1"
            />
            {/* Only the switch toggles: a FormControlLabel would make the whole
                full-width grid row a click target. */}
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={form.bootDefault}
                onChange={(e) => setForm({ ...form, bootDefault: e.target.checked })}
                slotProps={{ input: { 'aria-labelledby': 'boot-default-label' } }}
              />
              <Typography id="boot-default-label">
                {`设备开机时${form.bootDefault ? '打开' : '关闭'}`}
              </Typography>
            </Box>
            <Box
              sx={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button onClick={() => setForm(null)} disabled={saving}>
                取消
              </Button>
              <Button type="submit" variant="contained" disabled={saving || !formValid}>
                保存
              </Button>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {!form ? (
          <Button startIcon={<AddIcon />} onClick={() => setForm(EMPTY_FORM)}>
            添加控制点
          </Button>
        ) : null}
        <Button onClick={handleClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  )
}
